import React, { useEffect, useState } from 'react'
import { Button, Checkbox, ConfigProvider, DatePicker, Form, Input, InputNumber, Modal, Radio, Switch, Tree } from 'antd'
import { useTranslation } from 'react-i18next'
import { CheckboxGroupGrid, FormButton } from '../group.styled'
import { addGroup, modifyGroup } from '../../../apis/group'
import dayjs from 'dayjs'
import { fetchApiKeyPermissions } from '../../../apis/apiKey'

function GroupModal (props) {
  const { show, setShow, successCallback = () => {}, data, dataSource = {}, menus = [] } = props
  const { t: ln_t } = useTranslation()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [permission, setPermission] = useState(false)
  const [apiKeyPermissionsOpt, setApiKeyPermissionsOpt] = useState(false)
  const isAdminGroup = Form.useWatch('groupType', form)

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`group.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`group.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })
  const statusOptions = [{ value: 1, label: ln_t('util.enable') }, { value: 0, label: ln_t('util.disable') }]

  const handleClose = () => {
    setShow(false)
    setPermission()
    form.resetFields()
  }

  const handleAdd = async reqBody => {
    setIsLoading(true)
    const res = await addGroup(reqBody)
    if (res?.status === 201) {
      successCallback()
      handleClose()
    }
    setIsLoading(false)
  }
  const handleMod = async reqBody => {
    setIsLoading(true)
    const res = await modifyGroup(data?.id, reqBody)
    if (res?.status === 200) {
      successCallback()
      handleClose()
    }
    setIsLoading(false)
  }

  const handleSave = ({ userLimit, brandLimit, segmentLimit, targetingLimit, genBiQuotaLimit, invoiceDashboardLimit, timeRange, dataSource, permission, apiKeyPermissions, groupType, ...other }) => {
    const functions = permission?.map(i => i?.split('-').slice(-1))?.join(',')
    const adminLimit = groupType ? {} : { invoiceDashboardLimit }
    const reqBody = {
      ...other,
      limits: { userLimit, brandLimit, segmentLimit, genBiQuotaLimit, targetingLimit, ...adminLimit },
      startDate: dayjs(timeRange[0]).format('YYYY-MM-DD 00:00:00'),
      endDate: dayjs(timeRange[1]).format('YYYY-MM-DD 00:00:00'),
      dataSourceTypes: dataSource?.join(','),
      apiKeyPermissions: apiKeyPermissions?.join(','),
      groupType: groupType ? 1 : 0,
      functions
    }
    data?.addMode ? handleAdd(reqBody) : handleMod(reqBody)
  }

  const constructTree = data => {
    const parent = data?.filter(i => i.parentId === 0)
    return parent?.map(i => ({
      title: ln_t(`sidebar.${i.key}`),
      key: `0-${i.id}`,
      children: data?.filter(j => j?.parentId === i.id)?.map(j => ({
        title: ln_t(`sidebar.${j.key}`),
        key: `0-${j.id}`
      }))
    }))
  }

  const getApiKeyPermissionOptions = async () => {
    const res = await fetchApiKeyPermissions()
    if (res?.status === 200) setApiKeyPermissionsOpt(res?.data?.map(i => ({ value: `${i.id}`, label: i.name })))
  }

  useEffect(() => {
    getApiKeyPermissionOptions()
    if (!show || data?.addMode) return form.resetFields()
    const { limits, startDate, endDate, dataSourceTypes, functions, apiKeyPermissions, ...other } = data
    const timeRange = [dayjs(startDate), dayjs(endDate)]
    const managementItems = []
    const func = functions?.split(',')?.map(i => {
      const { id, link = '' } = menus?.find(j => `${j.id}` === `${i}`) || {}
      if (link.includes('management')) managementItems.push(id)
      return `0-${i}`
    })
    const managementNotComplete = menus.filter(i => i.link.includes('management') && i.key !== 'management').some(i => !managementItems.includes(i.id))
    const managementId = menus.find(i => i.key === 'management')?.id
    const formattedFunc = managementNotComplete ? func?.filter(i => i !== `0-${managementId}`) : func
    setPermission(formattedFunc)
    form.setFieldsValue({ ...other, ...limits, timeRange, dataSource: dataSourceTypes?.split(','), permission: formattedFunc, apiKeyPermissions: apiKeyPermissions?.split(',') })
  }, [show])

  return (
    <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
      <Modal title={`${ln_t(`util.${data?.addMode ? 'add' : 'edit'}`)} ${ln_t('group.group')}`} open={show} onCancel={handleClose} footer={null} width={800} style={{ top: '16px' }}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)} style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex' }}>
            <div style={{ width: '50%', paddingRight: '16px', borderRight: '1px solid rgba(0,0,0,.1)' }}>
              <Form.Item {...defConfig('name', 'input')}>
                <Input placeholder={defPlaceholder('name', 'input')} />
              </Form.Item>
              <Form.Item {...defConfig('englishName', 'input')} name="eName">
                <Input placeholder={defPlaceholder('englishName', 'input')} />
              </Form.Item>
              <Form.Item {...defConfig('timeRange', 'input')}>
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item {...defConfig('userLimit', 'input')}>
                <InputNumber placeholder={defPlaceholder('userLimit', 'input')} precision={0} min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item {...defConfig('brandLimit', 'input')}>
                <InputNumber placeholder={defPlaceholder('brandLimit', 'input')} precision={0} min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item {...defConfig('segmentLimit', 'input')}>
                <InputNumber placeholder={defPlaceholder('segmentLimit', 'input')} precision={0} min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item {...defConfig('targetingLimit', 'input')}>
                <InputNumber placeholder={defPlaceholder('targetingLimit', 'input')} precision={0} min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item {...defConfig('genBiQuotaLimit', 'input', false)}>
                <InputNumber placeholder={defPlaceholder('genBiQuotaLimit', 'input')} precision={0} min={0} style={{ width: '100%' }} />
              </Form.Item>
              {!isAdminGroup && <Form.Item {...defConfig('invoiceDashboardLimit', 'input')}>
                <InputNumber placeholder={defPlaceholder('invoiceDashboardLimit', 'input')} precision={0} min={0} style={{ width: '100%' }} />
              </Form.Item>}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                <Form.Item name="groupType" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
                <div>{ln_t('group.groupType')}</div>
              </div>
              <CheckboxGroupGrid grid={2}>
                <Form.Item {...defConfig('apiKeyPermissions', 'select', false)}>
                  <Checkbox.Group options={apiKeyPermissionsOpt} />
                </Form.Item>
              </CheckboxGroupGrid>
              <Form.Item {...defConfig('status', 'select')} initialValue={1}>
                <Radio.Group options={statusOptions} optionType="button" buttonStyle="solid"/>
              </Form.Item>
            </div>
            <div style={{ width: '50%', paddingLeft: '16px' }}>
              <CheckboxGroupGrid grid={3}>
                <Form.Item {...defConfig('dataSource', 'select')}>
                  <Checkbox.Group options={Object.entries(dataSource)?.map(([value, label]) => ({ value, label }))} />
                </Form.Item>
              </CheckboxGroupGrid>
              <Form.Item {...defConfig('permission', 'input')}>
                <Tree
                  checkable
                  defaultExpandAll
                  checkedKeys={permission}
                  treeData={constructTree(menus)}
                  onCheck={e => { form.setFieldValue('permission', e); setPermission(e) }}
                />
              </Form.Item>
            </div>
          </div>
          <FormButton style={{ marginTop: '16px' }}>
            <Button type="default" onClick={handleClose}>{ln_t('util.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>{ln_t('util.confirm')}</Button>
          </FormButton>
        </Form>
      </Modal>
    </ConfigProvider>
  )
}

export default GroupModal
